/**
 * @fileoverview Per-section HTML renderers — converts DocumentRenderData sections into
 * deterministic HTML fragments matching the existing Handlebars template output.
 * @module document-render/renderSections
 *
 * Responsibilities: - Produce HTML for each document section (header, recipient, subject,
 *                     salutation, body, metadata, violations, photos, comments, contact,
 *                     signatures, legal, footer, static content, advanced fields, form responses).
 *                   - Match the CSS class naming convention and HTML structure of the worker
 *                     Handlebars templates so the visual output is identical.
 * Dependencies:     - ./documentConfig (Resolved* types)
 *                   - ./renderPrimitives (renderCheckboxHtml for violation checklists)
 * Architecture:     - Pure functions — no I/O, no side effects, no framework imports.
 *                     Each function takes a Resolved* object and returns an HTML string.
 *                     Called by renderDocumentLayoutToHtml to expand dynamic bands.
 * Notes:            - HTML in body paragraphs and static content is assumed pre-sanitized
 *                     by the data layer (worker sanitizer or DOMPurify on the frontend).
 *                   - Cover page (kind: coverPage) is rendered by renderCoverPage() — B2.
 */

import type {
  DocumentConfig,
  ResolvedHeader,
  ResolvedRecipientBlock,
  ResolvedSubjectLine,
  ResolvedSalutation,
  ResolvedBodyParagraph,
  ResolvedMetadataField,
  ResolvedViolationItem,
  ResolvedViolationGroup,
  ResolvedPhoto,
  ResolvedComments,
  ResolvedContactBlock,
  ResolvedSignatures,
  ResolvedLegalText,
  ResolvedFooter,
  ResolvedStaticContent,
  ResolvedAdvancedField,
  ResolvedFormResponseGroup,
  DocumentRenderData,
} from './documentConfig';
import type { DocumentLayout, Band } from './documentLayout';
import { renderCheckboxHtml, renderViolationMarkHtml, violationItemPassed, VIOLATION_RESULT_COLORS } from './renderPrimitives';
import type { CheckboxMarkStyle } from './renderPrimitives';
import { resolveAdvancedTokensInHtml } from './advancedTokens';
import { bandStyleCss, elementStyleCss } from './bandStyle';

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function esc(s: string | undefined | null): string {
  if (!s) return '';
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Pure clone used when participant config suppresses correction details. */
function withoutCorrectionDetails(data: DocumentRenderData): DocumentRenderData {
  const strip = (item: ResolvedViolationItem): ResolvedViolationItem => ({
    ...item,
    occurrences: item.occurrences?.map(({ correctionStatus: _status, fixedAt: _fixedAt, fixedNotes: _notes, ...occurrence }) => occurrence),
  });
  return {
    ...data,
    violations: data.violations?.map(strip),
    violationGroups: data.violationGroups?.map((group) => ({ ...group, items: group.items.map(strip) })),
  };
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

export function renderHeader(h: ResolvedHeader): string {
  const parts: string[] = [];
  parts.push('<header class="report-header">');
  parts.push('<div class="header-row">');

  if (h.logoUrl) {
    parts.push(`<div class="header-logo header-logo-${esc(h.logoPosition || 'left')}">`);
    parts.push(`<img src="${esc(h.logoUrl)}" alt="Department Logo" class="logo-img">`);
    parts.push('</div>');
  }

  parts.push('<div class="header-center">');
  if (h.departmentName) parts.push(`<div class="header-dept-name">${esc(h.departmentName)}</div>`);
  if (h.departmentAddress) parts.push(`<div class="header-dept-detail">${esc(h.departmentAddress)}</div>`);
  if (h.departmentPhone) parts.push(`<div class="header-dept-detail">Phone: ${esc(h.departmentPhone)}</div>`);
  if (h.departmentFax) parts.push(`<div class="header-dept-detail">Fax: ${esc(h.departmentFax)}</div>`);
  if (h.departmentWebsite) parts.push(`<div class="header-dept-detail">${esc(h.departmentWebsite)}</div>`);
  parts.push('</div>');

  if (h.secondLogoUrl) {
    parts.push(`<div class="header-logo header-logo-${esc(h.secondLogoPosition || 'right')}">`);
    parts.push(`<img src="${esc(h.secondLogoUrl)}" alt="Secondary Logo" class="logo-img">`);
    parts.push('</div>');
  }

  parts.push('</div>');
  if (h.title) {
    const ts = h.titleStyle ? ` style="${esc(elementStyleCss(h.titleStyle))}"` : '';
    parts.push(`<h1 class="report-title"${ts}>${esc(h.title)}</h1>`);
  }
  if (h.subtitle) {
    const ss = h.subtitleStyle ? ` style="${esc(elementStyleCss(h.subtitleStyle))}"` : '';
    parts.push(`<div class="report-subtitle"${ss}>${esc(h.subtitle)}</div>`);
  }
  parts.push('</header>');
  return parts.join('\n');
}

// ---------------------------------------------------------------------------
// Recipient Block
// ---------------------------------------------------------------------------

export function renderRecipientBlock(rb: ResolvedRecipientBlock): string {
  const parts: string[] = [];
  parts.push('<section class="report-section recipient-section">');

  if (rb.style === 'letter-address') {
    parts.push('<div class="letter-address-block">');
    for (const f of rb.fields) {
      parts.push(`<div class="letter-address-line">${esc(f.value)}</div>`);
    }
    parts.push('</div>');
  } else {
    // Default: grid (table)
    parts.push('<table class="info-table recipient-grid">');
    for (const f of rb.fields) {
      parts.push(`<tr><th>${esc(f.label)}</th><td>${esc(f.value)}</td></tr>`);
    }
    parts.push('</table>');
  }

  parts.push('</section>');
  return parts.join('\n');
}

// ---------------------------------------------------------------------------
// Subject Line
// ---------------------------------------------------------------------------

export function renderSubjectLine(sl: ResolvedSubjectLine): string {
  return `<section class="report-section subject-section">\n<div class="subject-line"><strong>${esc(sl.text)}</strong></div>\n</section>`;
}

// ---------------------------------------------------------------------------
// Salutation
// ---------------------------------------------------------------------------

export function renderSalutation(s: ResolvedSalutation): string {
  return `<section class="report-section salutation-section">\n<p class="salutation">${esc(s.text)}</p>\n</section>`;
}

// ---------------------------------------------------------------------------
// Body Text
// ---------------------------------------------------------------------------

export function renderBodyText(paragraphs: ResolvedBodyParagraph[]): string {
  const parts: string[] = [];
  parts.push('<section class="report-section body-text-section">');
  for (const p of paragraphs) {
    if (p.isHtml) {
      // HTML content is assumed pre-sanitized by the data layer.
      parts.push(`<div class="body-paragraph">${p.content}</div>`);
    } else {
      parts.push(`<p class="body-paragraph">${esc(p.content)}</p>`);
    }
  }
  parts.push('</section>');
  return parts.join('\n');
}

// ---------------------------------------------------------------------------
// Metadata
// ---------------------------------------------------------------------------

export function renderMetadata(fields: ResolvedMetadataField[]): string {
  const parts: string[] = [];
  parts.push('<section class="report-section metadata-section">');
  parts.push('<table class="info-table metadata-grid">');
  for (const f of fields) {
    const style = f.color ? ` style="color:${esc(f.color)};font-weight:600;"` : '';
    parts.push(`<tr><th>${esc(f.label)}</th><td${style}>${esc(f.value)}</td></tr>`);
  }
  parts.push('</table>');
  parts.push('</section>');
  return parts.join('\n');
}

// ---------------------------------------------------------------------------
// Violations
// ---------------------------------------------------------------------------

/**
 * Pass/fail coloring options resolved from the violation config (G-VIOL-COLOR).
 * `markStyle` selects the checkbox glyph; `coloring` enables pass-green/fail-red text;
 * `passColor`/`failColor` are the resolved hex colors (config override → canonical default).
 */
interface ViolationColorOptions {
  markStyle: CheckboxMarkStyle;
  coloring: boolean;
  passColor: string;
  failColor: string;
}

function resolveViolationColorOptions(config?: Record<string, unknown>): ViolationColorOptions {
  const markStyle = (config?.checkboxMarkStyle as CheckboxMarkStyle) || 'box';
  return {
    markStyle,
    coloring: config?.resultColoring === true,
    passColor: (config?.passColor as string) || VIOLATION_RESULT_COLORS.pass,
    failColor: (config?.failColor as string) || VIOLATION_RESULT_COLORS.fail,
  };
}

/** Inline text-color style applied to an item when result coloring is on (empty otherwise). */
function resultTextStyle(passed: boolean, opts: ViolationColorOptions): string {
  if (!opts.coloring) return '';
  return ` style="color:${passed ? opts.passColor : opts.failColor};"`;
}

/**
 * Render the photos attached to a violation beneath it for the inline layout (DFE-005). Emitted
 * inside the violation row so each photo sits with the violation it belongs to instead of in the
 * flat gallery. Photos with no resolvable URL are skipped so broken-image icons never appear.
 */
function renderInlineViolationPhotosHtml(photos: ResolvedPhoto[] | undefined): string {
  if (!photos || photos.length === 0) return '';
  const renderable = photos.filter((p) => p.blobUrl);
  if (renderable.length === 0) return '';
  const parts: string[] = ['<div class="violation-inline-photos">'];
  for (const p of renderable) {
    parts.push('<div class="photo-item violation-photo">');
    parts.push(`<img src="${esc(p.blobUrl)}" alt="${esc(p.caption)}">`);
    parts.push('</div>');
  }
  parts.push('</div>');
  return parts.join('\n');
}

/**
 * Resolve the display label and value for the "Repaired" column in the occurrence date row.
 * Maps `correctionStatus` to a human-readable label + value based on the occurrence lifecycle.
 */
function resolveRepairedDisplay(
  correctionStatus: string | undefined,
  fixedAt: string | undefined,
): { label: string; value: string } {
  switch (correctionStatus) {
    case 'repaired':
      return { label: 'Repaired on', value: fixedAt || '—' };
    case 'exception':
      return { label: 'Status', value: 'Exception Granted' };
    case 'extensionGranted':
      return { label: 'Status', value: 'Extension Granted' };
    default:
      return { label: 'Repaired on', value: 'Violation Not Repaired' };
  }
}

/**
 * Render a single violation as a checklist row (mark + code + description + optional reference).
 * Shared by the flat checklist layout and the question-grouped layout so both emit identical
 * item markup (G-DRY-RENDER). Per-occurrence bullets (repair data, spatial info, notes) are
 * opt-in via `showOccurrences`; enabled for all checklist-family layouts (flat, zone, category,
 * severity, question-grouped). Narrative and table formats use separate rendering paths.
 */
function renderChecklistItemHtml(
  item: ResolvedViolationItem,
  opts: ViolationColorOptions,
  showOccurrences = false,
): string {
  const passed = violationItemPassed(item);
  const textStyle = resultTextStyle(passed, opts);
  const parts: string[] = ['<div class="checklist-item">'];
  parts.push(renderViolationMarkHtml(passed, opts.markStyle, {
    checked: item.checked !== false,
    colored: opts.coloring,
    passColor: opts.passColor,
    failColor: opts.failColor,
  }));
  parts.push(`<span class="checklist-code"${textStyle}>${esc(item.fireCode || item.code)}</span>`);
  parts.push(`<span class="checklist-desc"${textStyle}>${esc(item.description)}</span>`);
  if (item.ieCode) parts.push(`<span class="checklist-ref">IE: ${esc(item.ieCode)}</span>`);
  if (item.text) parts.push(`<span class="checklist-text">${esc(item.text)}</span>`);
  if (item.codeReference && item.codeReference !== item.ieCode) {
    parts.push(`<span class="checklist-ref">(${esc(item.codeReference)})</span>`);
  }
  parts.push('</div>');
  // Occurrences (G-VIOL-QGROUP): list each recorded location beneath the code — question layout only.
  // Each occurrence renders spatial info (zone/location), inspector notes, repair date row, and
  // per-occurrence inline photos. Occurrences with no renderable detail are skipped so empty
  // <ul> tags never produce stray whitespace.
  if (showOccurrences && item.occurrences && item.occurrences.length > 0) {
    const occurrenceItems: string[] = [];
    for (const occ of item.occurrences) {
      const spatial = [occ.zone, occ.location].filter(Boolean).map((s) => esc(String(s)));
      const hasRepairData = occ.correctionStatus || occ.discoveredAt || occ.fixedAt;
      if (spatial.length === 0 && !occ.notes && !hasRepairData) continue;
      const occParts: string[] = ['<li class="violation-occurrence">'];
      if (spatial.length > 0) {
        occParts.push(`<div class="occ-spatial">${spatial.join(' — ')}</div>`);
      }
      if (occ.notes) {
        occParts.push(`<div class="occ-notes"><span class="occ-label">Notes:</span> ${esc(String(occ.notes))}</div>`);
      }
      if (hasRepairData) {
        const repairedDisplay = resolveRepairedDisplay(occ.correctionStatus, occ.fixedAt);
        occParts.push('<div class="occ-date-row">');
        occParts.push(`<div class="occ-date-cell"><span class="occ-date-label">Found on</span><span class="occ-date-value">${esc(occ.discoveredAt || '—')}</span></div>`);
        occParts.push(`<div class="occ-date-cell"><span class="occ-date-label">${esc(repairedDisplay.label)}</span><span class="occ-date-value">${esc(repairedDisplay.value)}</span></div>`);
        occParts.push('</div>');
      }
      if (occ.fixedNotes) {
        occParts.push(`<div class="occ-notes"><span class="occ-label">Repair Notes:</span> ${esc(String(occ.fixedNotes))}</div>`);
      }
      // Per-occurrence inline photos (DFE-005): render beneath this occurrence's repair data
      // so each photo sits with the occurrence it belongs to.
      const occPhotoHtml = renderInlineViolationPhotosHtml(occ.photos);
      if (occPhotoHtml) occParts.push(occPhotoHtml);
      occParts.push('</li>');
      occurrenceItems.push(occParts.join('\n'));
    }
    if (occurrenceItems.length > 0) {
      parts.push('<ul class="violation-occurrences">');
      parts.push(...occurrenceItems);
      parts.push('</ul>');
    }
  }
  // Inline photos (DFE-005): when the photo layout is 'inline', the assembler attaches this
  // violation's photos onto item.photos so they render beneath the violation rather than in the
  // flat gallery. When showOccurrences is true, per-occurrence photos are already rendered above;
  // only render item-level photos that are true residuals (not already on any occurrence) to
  // avoid duplicating photos that distributeInlinePhotos placed on both occ.photos and item.photos.
  if (showOccurrences) {
    const occPhotoSet = new Set<ResolvedPhoto>();
    for (const occ of item.occurrences ?? []) {
      for (const p of occ.photos ?? []) occPhotoSet.add(p);
    }
    const residualPhotos = (item.photos ?? []).filter((p) => !occPhotoSet.has(p));
    const residualHtml = renderInlineViolationPhotosHtml(residualPhotos.length > 0 ? residualPhotos : undefined);
    if (residualHtml) parts.push(residualHtml);
  } else {
    const inlinePhotosHtml = renderInlineViolationPhotosHtml(item.photos);
    if (inlinePhotosHtml) parts.push(inlinePhotosHtml);
  }
  // Separator between violations for visual clarity in the question-grouped layout.
  if (showOccurrences) parts.push('<div class="violation-separator"></div>');
  return parts.join('\n');
}

function renderViolationsChecklist(
  groups: ResolvedViolationGroup[],
  hasViolations: boolean,
  config?: Record<string, unknown>,
): string {
  const opts = resolveViolationColorOptions(config);
  const parts: string[] = [];
  parts.push('<section class="report-section violations-section">');
  for (const g of groups) {
    parts.push('<div class="zone-group">');
    if (g.groupLabel) parts.push(`<h3 class="zone-title">${esc(g.groupLabel)}</h3>`);
    for (const item of g.items) {
      parts.push(renderChecklistItemHtml(item, opts, true));
    }
    parts.push('</div>');
  }
  if (!hasViolations) {
    parts.push('<p class="no-violations">No violations found during this inspection.</p>');
  }
  parts.push('</section>');
  return parts.join('\n');
}

/**
 * Question-grouped layout (G-VIOL-QGROUP): each group is an inspection question heading with its
 * violations (code + occurrences) nested beneath, mirroring the inspection-template rendering.
 * Violations with no parent question are collected under an "Additional Violations" heading (the
 * group flagged `isAdditional`, produced by groupViolationsByQuestion).
 */
function renderViolationsByQuestion(
  groups: ResolvedViolationGroup[],
  hasViolations: boolean,
  config?: Record<string, unknown>,
): string {
  const opts = resolveViolationColorOptions(config);
  const parts: string[] = [];
  parts.push('<section class="report-section violations-section violations-by-question">');
  for (const g of groups) {
    const groupClass = g.isAdditional ? 'question-group additional-violations' : 'question-group';
    parts.push(`<div class="${groupClass}">`);
    if (g.groupLabel) parts.push(`<h3 class="question-title">${esc(g.groupLabel)}</h3>`);
    for (const item of g.items) {
      parts.push(renderChecklistItemHtml(item, opts, true));
    }
    parts.push('</div>');
  }
  if (!hasViolations) {
    parts.push('<p class="no-violations">No violations found during this inspection.</p>');
  }
  parts.push('</section>');
  return parts.join('\n');
}

function renderViolationsNarrative(
  groups: ResolvedViolationGroup[],
  hasViolations: boolean,
  config?: Record<string, unknown>,
): string {
  const opts = resolveViolationColorOptions(config);
  const parts: string[] = [];
  parts.push('<section class="report-section violations-section">');
  for (const g of groups) {
    if (g.groupLabel) parts.push(`<h3 class="zone-title">${esc(g.groupLabel)}</h3>`);
    for (const item of g.items) {
      const passed = violationItemPassed(item);
      const textStyle = resultTextStyle(passed, opts);
      parts.push('<div class="violation-item">');
      parts.push(`<span class="violation-code"${textStyle}>${esc(item.fireCode || item.code)}</span>: <span class="violation-desc"${textStyle}>${esc(item.description)}</span>`);
      if (item.ieCode) parts.push(`<span class="violation-ref">IE: ${esc(item.ieCode)}</span>`);
      if (item.text) parts.push(`<div class="violation-text">${esc(item.text)}</div>`);
      if (item.codeReference && item.codeReference !== item.ieCode) {
        parts.push(`<span class="violation-ref">[${esc(item.codeReference)}]</span>`);
      }
      if (item.location) {
        parts.push(`<div class="violation-location"><em>Location: ${esc(item.location)}</em></div>`);
      }
      // Per-occurrence repair data beneath the violation paragraph.
      if (item.occurrences && item.occurrences.length > 0) {
        for (const occ of item.occurrences) {
          const spatial = [occ.zone, occ.location].filter(Boolean).map((s) => esc(String(s)));
          const hasRepairData = occ.correctionStatus || occ.discoveredAt || occ.fixedAt;
          if (spatial.length === 0 && !occ.notes && !hasRepairData) continue;
          parts.push('<div class="narrative-occurrence">');
          if (spatial.length > 0) {
            parts.push(`<div class="occ-spatial">${spatial.join(' — ')}</div>`);
          }
          if (occ.notes) {
            parts.push(`<div class="occ-notes"><span class="occ-label">Notes:</span> ${esc(String(occ.notes))}</div>`);
          }
          if (hasRepairData) {
            const repairedDisplay = resolveRepairedDisplay(occ.correctionStatus, occ.fixedAt);
            parts.push('<div class="occ-date-row">');
            parts.push(`<div class="occ-date-cell"><span class="occ-date-label">Found on</span><span class="occ-date-value">${esc(occ.discoveredAt || '—')}</span></div>`);
            parts.push(`<div class="occ-date-cell"><span class="occ-date-label">${esc(repairedDisplay.label)}</span><span class="occ-date-value">${esc(repairedDisplay.value)}</span></div>`);
            parts.push('</div>');
          }
          if (occ.fixedNotes) {
            parts.push(`<div class="occ-notes"><span class="occ-label">Repair Notes:</span> ${esc(String(occ.fixedNotes))}</div>`);
          }
          const occPhotoHtml = renderInlineViolationPhotosHtml(occ.photos);
          if (occPhotoHtml) parts.push(occPhotoHtml);
          parts.push('</div>');
        }
      }
      // Inline photos (DFE-005) — see renderChecklistItemHtml; attached by the assembler for the
      // inline layout so the photo renders with its violation in the narrative layout too.
      const inlinePhotosHtml = renderInlineViolationPhotosHtml(item.photos);
      if (inlinePhotosHtml) parts.push(inlinePhotosHtml);
      parts.push('</div>');
    }
  }
  if (!hasViolations) {
    parts.push('<p class="no-violations">No violations were observed during this inspection.</p>');
  }
  parts.push('</section>');
  return parts.join('\n');
}

function renderViolationsTable(
  items: ResolvedViolationItem[],
  hasViolations: boolean,
  config?: Record<string, unknown>,
): string {
  const showRef = config?.showCodeReferences !== false;
  const showLoc = config?.showLocationDescriptions === true;
  const hasFireCode = items.some((item) => item.fireCode);
  const hasIeCode = items.some((item) => item.ieCode);
  const hasText = items.some((item) => item.text);
  const hasCodeReference = showRef && items.some((item) => item.codeReference);
  const hasLocation = showLoc && items.some((item) =>
    item.location || (item.occurrences ?? []).some((o) => o.zone || o.location),
  );
  const opts = resolveViolationColorOptions(config);
  const parts: string[] = [];
  parts.push('<section class="report-section violations-section">');
  parts.push('<table class="info-table violation-table">');
  parts.push('<thead><tr>');
  parts.push(`<th>${hasFireCode ? 'Fire Code' : 'Code'}</th>`);
  if (hasIeCode) parts.push('<th>IE Code</th>');
  parts.push('<th>Description</th>');
  if (hasText) parts.push('<th>Text</th>');
  if (hasCodeReference) parts.push('<th>Reference</th>');
  if (hasLocation) parts.push('<th>Location</th>');
  parts.push('<th>Status</th><th>Found</th><th>Repaired</th>');
  parts.push('</tr></thead>');
  parts.push('<tbody>');
  for (const item of items) {
    const passed = violationItemPassed(item);
    const statusStyle = opts.coloring
      ? ` style="color:${passed ? opts.passColor : opts.failColor};font-weight:bold;"`
      : '';
    // Render one row per occurrence; fall back to a single row if no occurrences exist.
    const occs = item.occurrences && item.occurrences.length > 0 ? item.occurrences : [undefined];
    for (let i = 0; i < occs.length; i++) {
      const occ = occs[i];
      parts.push('<tr>');
      if (i === 0) {
        const rowspan = occs.length > 1 ? ` rowspan="${occs.length}"` : '';
        parts.push(`<td class="violation-code"${rowspan}>${esc(item.fireCode || item.code)}</td>`);
        if (hasIeCode) parts.push(`<td${rowspan}>${esc(item.ieCode)}</td>`);
        parts.push(`<td${rowspan}>${esc(item.description)}</td>`);
        if (hasText) parts.push(`<td${rowspan}>${esc(item.text)}</td>`);
        if (hasCodeReference) parts.push(`<td${rowspan}>${esc(item.codeReference)}</td>`);
      }
      if (hasLocation) {
        const loc = occ ? [occ.zone, occ.location].filter(Boolean).join(' — ') : (item.location || '');
        parts.push(`<td>${esc(loc)}</td>`);
      }
      parts.push(`<td class="violation-status"${statusStyle}>${esc(occ?.correctionStatus || item.status)}</td>`);
      const repairedDisplay = occ ? resolveRepairedDisplay(occ.correctionStatus, occ.fixedAt) : undefined;
      parts.push(`<td>${esc(occ?.discoveredAt || '—')}</td>`);
      parts.push(`<td>${esc(repairedDisplay?.value || '—')}</td>`);
      parts.push('</tr>');
    }
  }
  parts.push('</tbody></table>');
  if (!hasViolations) {
    parts.push('<p class="no-violations">No violations found.</p>');
  }
  parts.push('</section>');
  return parts.join('\n');
}

export function renderViolations(data: DocumentRenderData): string {
  const displayFormat = data.documentConfig.violationConfig?.displayFormat || 'checklist';
  const groupBy = data.documentConfig.violationConfig?.groupBy;
  const hasViolations = data.hasViolations ?? (data.violations?.length ?? 0) > 0;
  const violationConfig = data.documentConfig.violationConfig as Record<string, unknown> | undefined;

  // Question grouping (G-VIOL-QGROUP) nests violations (code + occurrences) under their inspection
  // question, with an Additional Violations bucket. It is a checklist-family layout, so it only
  // applies to checklist/grouped formats; a stale `groupBy:'question'` left over after switching to
  // narrative/table must NOT override those formats (the builder hides the groupBy control there).
  if (groupBy === 'question' && displayFormat !== 'narrative' && displayFormat !== 'table') {
    return renderViolationsByQuestion(data.violationGroups || [], hasViolations, violationConfig);
  }

  if (displayFormat === 'narrative') {
    return renderViolationsNarrative(data.violationGroups || [], hasViolations, violationConfig);
  }
  if (displayFormat === 'table') {
    return renderViolationsTable(data.violations || [], hasViolations, violationConfig);
  }
  // Default: checklist
  return renderViolationsChecklist(data.violationGroups || [], hasViolations, violationConfig);
}

// ---------------------------------------------------------------------------
// Photos
// ---------------------------------------------------------------------------

export function renderPhotos(photos: ResolvedPhoto[], config?: DocumentConfig): string {
  // Filter out photos with no resolvable URL so broken-image icons never appear in the output.
  const renderable = photos.filter(p => p.blobUrl);
  if (!renderable.length) return '';
  const pc = config?.photoConfig;
  const layoutClass = pc?.layout ? ` photo-layout-${esc(String(pc.layout))}` : '';
  const sizeClass = pc?.thumbnailSize ? ` photo-size-${esc(String(pc.thumbnailSize))}` : '';
  const parts: string[] = [];
  parts.push(`<section class="report-section photos-section">`);
  parts.push(`<div class="photo-gallery${layoutClass}">`);
  for (const p of renderable) {
    const extraClass = (p as any).isViolationPhoto ? ' violation-photo' : '';
    parts.push(`<div class="photo-item${sizeClass}${extraClass}">`);
    parts.push(`<img src="${esc(p.blobUrl)}" alt="${esc(p.caption)}">`);
    if ((p as any).isViolationPhoto && (p as any).violationCode) {
      const desc = (p as any).violationDescription ? ` \u2014 ${esc((p as any).violationDescription)}` : '';
      parts.push(`<div class="photo-violation-tag">Violation: ${esc((p as any).violationCode)}${desc}</div>`);
    }
    // Suppress caption for violation photos — occurrence detail already shows notes; caption
    // would duplicate that text. Non-violation photos keep their captions.
    if (p.caption && !(p as any).isViolationPhoto) parts.push(`<div class="photo-caption">${esc(p.caption)}</div>`);
    if (p.takenAt) parts.push(`<div class="photo-date">${esc(p.takenAt)}</div>`);
    parts.push('</div>');
  }
  parts.push('</div>');
  parts.push('</section>');
  return parts.join('\n');
}

// ---------------------------------------------------------------------------
// Comments
// ---------------------------------------------------------------------------

export function renderComments(c: ResolvedComments, sectionLabel?: string): string {
  const parts: string[] = [];
  parts.push('<section class="report-section comments-section">');
  if (sectionLabel) parts.push(`<h2 class="section-title">${esc(sectionLabel)}</h2>`);

  if (c.externalNotes) {
    parts.push('<div class="comments-box">');
    parts.push('<div class="comments-label">Comments:</div>');
    parts.push(`<div class="comments-text">${esc(c.externalNotes)}</div>`);
    parts.push('</div>');
  } else {
    parts.push('<div class="comments-box comments-empty">');
    parts.push('<div class="comments-label">Comments:</div>');
    parts.push('<div class="comments-text">&nbsp;</div>');
    parts.push('</div>');
  }

  if (c.additionalFields) {
    for (const f of c.additionalFields) {
      parts.push('<div class="additional-field">');
      parts.push(`<span class="additional-field-label">${esc(f.label)}:</span>`);
      parts.push(`<span class="additional-field-value">${esc(f.value)}</span>`);
      parts.push('</div>');
    }
  }

  parts.push('</section>');
  return parts.join('\n');
}

// ---------------------------------------------------------------------------
// Contact Block
// ---------------------------------------------------------------------------

export function renderContactBlock(cb: ResolvedContactBlock): string {
  const parts: string[] = [];
  parts.push('<section class="report-section contact-section">');
  if (cb.introText) parts.push(`<p class="contact-intro">${esc(cb.introText)}</p>`);
  parts.push('<div class="contact-details">');
  if (cb.phone) parts.push(`<div class="contact-line">Phone: ${esc(cb.phone)}</div>`);
  if (cb.email) parts.push(`<div class="contact-line">Email: ${esc(cb.email)}</div>`);
  if (cb.fax) parts.push(`<div class="contact-line">Fax: ${esc(cb.fax)}</div>`);
  parts.push('</div>');
  parts.push('</section>');
  return parts.join('\n');
}

// ---------------------------------------------------------------------------
// Signatures
// ---------------------------------------------------------------------------

export function renderSignatures(sigs: ResolvedSignatures): string {
  const parts: string[] = [];
  parts.push(`<section class="report-section signatures-section">`);
  parts.push(`<div class="signature-block signature-style-${esc(sigs.style)}">`);

  for (const sig of sigs.signatures) {
    parts.push('<div class="signature-entry">');
    parts.push(`<div class="signature-label">${esc(sig.label)}</div>`);

    if (sigs.style === 'initials') {
      if (sig.signatureUrl) {
        parts.push(`<div class="signature-image signature-initials"><img src="${esc(sig.signatureUrl)}" alt="${esc(sig.label)} Initials"></div>`);
      } else {
        parts.push('<div class="signature-line signature-initials-line">______</div>');
      }
    } else if (sigs.style === 'digital') {
      if (sig.signatureUrl) {
        parts.push(`<div class="signature-image"><img src="${esc(sig.signatureUrl)}" alt="${esc(sig.label)} Signature"></div>`);
      } else {
        parts.push('<div class="signature-line">[Digital signature pending]</div>');
      }
    } else {
      // Full (default)
      if (sig.signatureUrl) {
        parts.push(`<div class="signature-image"><img src="${esc(sig.signatureUrl)}" alt="${esc(sig.label)} Signature"></div>`);
      } else {
        parts.push('<div class="signature-line">_________________________________</div>');
      }
    }

    if (sig.showPrintedName) {
      parts.push(sig.name
        ? `<div class="signature-name">${esc(sig.name)}</div>`
        : '<div class="signature-name">_________________________________</div>');
    }
    if (sig.showTitle && sig.title) {
      parts.push(`<div class="signature-title">${esc(sig.title)}</div>`);
    }
    if (sig.showDate) {
      parts.push(sig.signedAt
        ? `<div class="signature-date">Date: ${esc(sig.signedAt)}</div>`
        : '<div class="signature-date">Date: _____________</div>');
    }

    parts.push('</div>');
  }

  parts.push('</div>');
  parts.push('</section>');
  return parts.join('\n');
}

// ---------------------------------------------------------------------------
// Legal Text
// ---------------------------------------------------------------------------

export function renderLegalText(lt: ResolvedLegalText): string {
  if (!lt.content) return '';
  return `<section class="report-section legal-section legal-position-${esc(lt.position)}">\n<div class="legal-text">${esc(lt.content)}</div>\n</section>`;
}

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

export function renderFooter(f: ResolvedFooter): string {
  const parts: string[] = [];
  parts.push('<footer class="report-footer">');
  parts.push('<div class="footer-content">');
  if (f.departmentName) parts.push(`<span class="footer-dept">${esc(f.departmentName)}</span>`);
  if (f.departmentPhone) parts.push(`<span class="footer-phone"> | ${esc(f.departmentPhone)}</span>`);
  parts.push('</div>');
  if (f.customText) parts.push(`<div class="footer-custom">${esc(f.customText)}</div>`);
  parts.push('</footer>');
  return parts.join('\n');
}

// ---------------------------------------------------------------------------
// Static Content
// ---------------------------------------------------------------------------

export function renderStaticContent(sc: ResolvedStaticContent): string {
  const parts: string[] = [];
  parts.push('<section class="report-section static-content-section">');
  if (sc.label) parts.push(`<h2 class="section-title">${esc(sc.label)}</h2>`);

  if (sc.type === 'html' && sc.htmlContent) {
    // HTML content is assumed pre-sanitized by the data layer.
    parts.push(`<div class="static-html">${sc.htmlContent}</div>`);
  } else if (sc.type === 'uploadedFile') {
    parts.push(`<div class="static-attachment-note"><em>See attached: ${esc(sc.label)}</em></div>`);
  }

  parts.push('</section>');
  return parts.join('\n');
}

// ---------------------------------------------------------------------------
// Cover Page
// ---------------------------------------------------------------------------

/**
 * Renders a full-page cover sheet for the document.
 * Derives all data from existing DocumentRenderData fields:
 * logo and department name from header, entity info from recipientBlock,
 * inspection summary and compliance badge from metadataFields.
 *
 * @process renderCoverPage
 * Steps:          1. Read coverPage config (defaults to showing all elements)
 *                 2. Conditionally render logo, department name, entity info,
 *                    inspection summary table, and compliance-status badge
 * Inputs:         DocumentRenderData
 * Outputs:        HTML string — one page-break-isolated section
 * Idempotency:    REQUIRED (pure function, no side effects)
 */
export function renderCoverPage(data: DocumentRenderData): string {
  const cfg = data.documentConfig.coverPage ?? {};
  const showLogo = cfg.showLogo !== false;
  const showEntityInfo = cfg.showEntityInfo !== false;
  const showInspectionSummary = cfg.showInspectionSummary !== false;
  const showResultBadge = cfg.showResultBadge !== false;

  const parts: string[] = [];
  parts.push('<section class="report-section cover-page-section" style="page-break-after:always;break-after:page;min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:60px 40px;box-sizing:border-box;">');

  // Branding block — logo + department name
  if (showLogo || data.header?.departmentName) {
    parts.push('<div class="cover-header" style="text-align:center;margin-bottom:40px;">');
    if (showLogo && data.header?.logoUrl) {
      parts.push(`<img src="${esc(data.header.logoUrl)}" alt="Department Logo" class="cover-logo" style="max-height:100px;max-width:240px;object-fit:contain;margin-bottom:16px;" />`);
    }
    if (data.header?.departmentName) {
      parts.push(`<h1 class="cover-dept-name" style="font-size:22px;font-weight:700;letter-spacing:0.05em;text-transform:uppercase;margin:0 0 4px;">${esc(data.header.departmentName)}</h1>`);
    }
    if (data.header?.title) {
      parts.push(`<h2 class="cover-report-title" style="font-size:16px;font-weight:600;color:var(--brand-primary,#374151);margin:0;">${esc(data.header.title)}</h2>`);
    }
    parts.push('</div>');
  }

  // Result badge — compliance status (prominent, center)
  if (showResultBadge) {
    const statusField = data.metadataFields?.find((f) => f.key === 'complianceStatus');
    if (statusField) {
      const badgeColor = statusField.color ?? '#374151';
      parts.push(`<div class="cover-badge" style="border:3px solid ${esc(badgeColor)};border-radius:8px;padding:12px 32px;margin-bottom:36px;text-align:center;">`);
      parts.push(`<span class="cover-badge-text" style="font-size:20px;font-weight:700;color:${esc(badgeColor)};letter-spacing:0.04em;">${esc(statusField.value)}</span>`);
      parts.push('</div>');
    }
  }

  // Entity / property info
  if (showEntityInfo && data.recipientBlock?.fields.length) {
    parts.push('<div class="cover-entity-info" style="width:100%;max-width:480px;margin-bottom:32px;">');
    parts.push('<table style="width:100%;border-collapse:collapse;font-size:13px;">');
    for (const field of data.recipientBlock.fields) {
      parts.push(`<tr><td style="font-weight:600;padding:4px 12px 4px 0;color:#6b7280;white-space:nowrap;">${esc(field.label)}</td><td style="padding:4px 0;color:#111827;">${esc(field.value)}</td></tr>`);
    }
    parts.push('</table>');
    parts.push('</div>');
  }

  // Inspection summary
  if (showInspectionSummary && data.metadataFields?.length) {
    const summaryKeys = ['inspectionDate', 'inspectionType', 'inspector'];
    const summaryFields = data.metadataFields.filter((f) => summaryKeys.includes(f.key));
    if (summaryFields.length) {
      parts.push('<div class="cover-inspection-summary" style="width:100%;max-width:480px;border-top:1px solid #e5e7eb;padding-top:20px;">');
      parts.push('<table style="width:100%;border-collapse:collapse;font-size:13px;">');
      for (const field of summaryFields) {
        parts.push(`<tr><td style="font-weight:600;padding:4px 12px 4px 0;color:#6b7280;white-space:nowrap;">${esc(field.label)}</td><td style="padding:4px 0;color:#111827;">${esc(field.value)}</td></tr>`);
      }
      parts.push('</table>');
      parts.push('</div>');
    }
  }

  parts.push('</section>');
  return parts.join('\n');
}

// ---------------------------------------------------------------------------
// Advanced Fields
// ---------------------------------------------------------------------------

export function renderAdvancedFields(fields: ResolvedAdvancedField[], sectionLabel?: string): string {
  if (!fields.length) return '';
  const parts: string[] = [];
  parts.push('<div class="advanced-fields-section">');
  parts.push(`<h2 class="section-title">${esc(sectionLabel || 'Inspection Data')}</h2>`);
  parts.push('<table class="advanced-fields-table" style="width:100%;border-collapse:collapse;margin-bottom:16px;">');
  parts.push('<tbody>');

  for (const f of fields) {
    parts.push('<tr class="advanced-field-row" style="border-bottom:1px solid #eee;">');
    if (f.renderMode === 'checkbox') {
      parts.push(`<td style="padding:6px 10px;width:30%;font-weight:600;color:#555;vertical-align:top;">${esc(f.label)}</td>`);
      // Checkbox value is pre-rendered HTML from renderCheckboxHtml
      parts.push(`<td style="padding:6px 10px;">${f.value}</td>`);
    } else if (f.renderMode === 'badge') {
      parts.push(`<td style="padding:6px 10px;width:30%;font-weight:600;color:#555;vertical-align:top;">${esc(f.label)}</td>`);
      parts.push(`<td style="padding:6px 10px;"><span style="display:inline-block;padding:2px 8px;border-radius:4px;background:#e8f4fd;color:#1a73e8;font-size:10pt;font-weight:600;">${esc(f.value)}</span></td>`);
    } else if (f.renderMode === 'block') {
      parts.push(`<td colspan="2" style="padding:8px 10px;">`);
      parts.push(`<div style="font-weight:600;color:#555;margin-bottom:4px;">${esc(f.label)}</div>`);
      parts.push(`<div style="padding:6px 10px;background:#f9f9f9;border:1px solid #eee;border-radius:4px;">${esc(f.value)}</div>`);
      parts.push('</td>');
    } else {
      // Default: two-column label/value
      parts.push(`<td style="padding:6px 10px;width:30%;font-weight:600;color:#555;vertical-align:top;">${esc(f.label)}</td>`);
      parts.push(`<td style="padding:6px 10px;">${esc(f.value)}</td>`);
    }
    parts.push('</tr>');
  }

  parts.push('</tbody></table>');
  parts.push('</div>');
  return parts.join('\n');
}

// ---------------------------------------------------------------------------
// Form Responses
// ---------------------------------------------------------------------------

export function renderFormResponses(groups: ResolvedFormResponseGroup[]): string {
  if (!groups.length) return '';
  const parts: string[] = [];
  parts.push('<section class="report-section form-responses-section">');

  for (const g of groups) {
    parts.push('<div class="form-response-group">');
    if (g.formName) parts.push(`<h3 class="form-response-form-name">${esc(g.formName)}</h3>`);

    for (const sec of g.sectionGroups) {
      if (sec.sectionTitle) parts.push(`<h4 class="form-response-section-title">${esc(sec.sectionTitle)}</h4>`);
      parts.push('<table class="form-response-table">');
      for (const f of sec.fields) {
        const imgClass = f.isImage ? ' form-response-image-row' : '';
        parts.push(`<tr class="form-response-row${imgClass}">`);
        parts.push(`<th class="form-response-label">${esc(f.label)}</th>`);
        parts.push('<td class="form-response-value">');
        if (f.isImage) {
          if (f.imageDataUri) {
            parts.push(`<img src="${esc(f.imageDataUri)}" class="form-response-image" alt="${esc(f.label)}">`);
          } else {
            parts.push('<span class="form-response-no-image">[Image not available]</span>');
          }
        } else {
          parts.push(esc(f.value));
        }
        parts.push('</td></tr>');
      }
      parts.push('</table>');
    }

    parts.push('</div>');
  }

  parts.push('</section>');
  return parts.join('\n');
}

// ---------------------------------------------------------------------------
// Complex visit sections
// ---------------------------------------------------------------------------

/**
 * @process renderComplexVisitSummary
 * Steps: Read resolved summary data, apply explicit config flags, escape dynamic values, and emit HTML.
 * Inputs: DocumentRenderData with optional complexVisitSummary and summary config.
 * Outputs: Deterministic summary HTML or an honest empty-state message.
 * Failure Handling: Missing summary data is rendered as unavailable; no fabricated values.
 * Idempotency: REQUIRED — pure function.
 */
export function renderComplexVisitSummary(data: DocumentRenderData): string {
  const summary = data.complexVisitSummary;
  if (!summary) {
    return '<section class="report-section complex-visit-summary"><p class="empty-state">Complex visit summary data is not available.</p></section>';
  }
  const rows: string[] = [];
  if (data.documentConfig.complexVisitSummary?.showComplexName !== false) {
    rows.push(`<div><strong>Complex:</strong> ${esc(summary.complexName)}</div>`);
  }
  if (data.documentConfig.complexVisitSummary?.showAnchor !== false) {
    rows.push(`<div><strong>Anchor:</strong> ${esc(summary.anchorName)}</div>`);
  }
  if (data.documentConfig.complexVisitSummary?.showVisitDate !== false) {
    rows.push(`<div><strong>Visit date:</strong> ${esc(summary.visitDate)}</div>`);
  }
  if (data.documentConfig.complexVisitSummary?.showParticipantCounts !== false) {
    rows.push(`<div><strong>Governed participants:</strong> ${summary.governedCount}</div>`);
  }
  return `<section class="report-section complex-visit-summary"><h2>Complex Visit Summary</h2>${rows.join('')}</section>`;
}

/**
 * @process renderComplexParticipants
 * Steps: Sort and filter explicit participant records, render configured nested data,
 *   and escape every dynamic value. The root (anchor) always renders first; standalone
 *   participants follow the normal inspection path and never appear in the complex letter.
 * Inputs: DocumentRenderData with participants, nested case data, and participant config.
 * Outputs: Deterministic participant HTML with optional page breaks/empty state.
 * Failure Handling: Missing participants produce an honest empty-state message.
 * Idempotency: REQUIRED — pure function; nested data is cloned before correction suppression.
 */
export function renderComplexParticipants(data: DocumentRenderData): string {
  const config = data.documentConfig.complexParticipants;
  const participants = [...(data.complexParticipants ?? [])];
  const includeRootEntity = config?.includeRootEntity ?? true;
  const includeGovernedMembers = config?.includeGovernedMembers ?? true;
  const sortedParticipants = participants
    .map((participant, index) => ({ participant, index }))
    .sort((a, b) => {
      if (a.participant.isRoot !== b.participant.isRoot) return a.participant.isRoot ? -1 : 1;
      if (config?.entitySort === 'entityName') return a.participant.entityName.localeCompare(b.participant.entityName) || a.index - b.index;
      if (config?.entitySort === 'entityCategory') {
        return a.participant.entityCategory.localeCompare(b.participant.entityCategory) || a.participant.entityName.localeCompare(b.participant.entityName) || a.index - b.index;
      }
      return a.index - b.index;
    })
    .map(({ participant }) => participant);
  const parts: string[] = [];
  if (sortedParticipants.length === 0) {
    return '<section class="report-section complex-participants"><p class="empty-state">Complex participant data is not available.</p></section>';
  }
  for (const participant of sortedParticipants) {
    if (!includeRootEntity && participant.isRoot) continue;
    if (!includeGovernedMembers && !participant.isRoot) continue;
    if ((config?.startEachEntityOnNewPage ?? true) && parts.length > 0) {
      parts.push('<div style="page-break-before:always;break-before:page;"></div>');
    }
    const header = (config?.showEntityHeader ?? true)
      ? `<h2>${esc(participant.entityName)}</h2>`
      : '';
    const details: string[] = [];
    if ((config?.showEntityAddress ?? true) && participant.entityAddress) {
      details.push(`<div class="participant-address">${esc(participant.entityAddress)}</div>`);
    }
    if ((config?.showEntityStatus ?? true) && participant.entityStatus) {
      details.push(`<div class="participant-status">${esc(participant.entityStatus)}</div>`);
    }
    if (config?.showPermits !== false && participant.permits?.length) {
      details.push(`<ul class="participant-permits">${participant.permits.map((item) => `<li>${esc(item.label)}${item.status ? ` (${esc(item.status)})` : ''}</li>`).join('')}</ul>`);
    }
    if (config?.showAssets !== false && participant.assets?.length) {
      details.push(`<ul class="participant-assets">${participant.assets.map((item) => `<li>${esc(item.label)}${item.status ? ` (${esc(item.status)})` : ''}</li>`).join('')}</ul>`);
    }
    const nested = participant.data;
    const selected: string[] = [];
    if ((config?.showQuestionsResponses ?? true) && nested.formResponses?.length) selected.push(renderFormResponses(nested.formResponses));
    if (config?.showViolations !== false && nested.violations?.length) {
      const violationData = config?.showCorrections === false
        ? withoutCorrectionDetails(nested)
        : nested;
      selected.push(renderViolations(violationData));
    }
    if ((config?.showPhotos ?? true) && nested.photos?.length) selected.push(renderPhotos(nested.photos, nested.documentConfig));
    if ((config?.showSignatures ?? true) && nested.signatures) selected.push(renderSignatures(nested.signatures));
    parts.push(`<section class="report-section complex-participant" data-case-id="${esc(participant.caseId)}">${header}${details.join('')}${selected.join('')}</section>`);
  }
  return parts.join('\n');
}

// ---------------------------------------------------------------------------
// Master Section Dispatcher — renders all sections in config order
// ---------------------------------------------------------------------------

/**
 * @process renderAllSections
 * Steps:    Iterate documentConfig.sections in order, calling the appropriate section
 *           renderer for each enabled section. Produces the complete body HTML.
 * Inputs:   DocumentRenderData (resolved data + config).
 * Outputs:  HTML string for the document body (excluding page chrome).
 * Idempotency: REQUIRED (pure).
 */
export function renderAllSections(data: DocumentRenderData): string {
  const enabledSections = new Set(
    data.documentConfig.sections.filter((s) => s.enabled).map((s) => s.id),
  );
  const parts: string[] = [];

  for (const section of data.documentConfig.sections) {
    if (!section.enabled) continue;

    // Page break before (if the section config has it)
    if ((section as any).pageBreakBefore) {
      parts.push('<div style="page-break-before:always;break-before:page;"></div>');
    }

    switch (section.id) {
      case 'complexVisitSummary':
        parts.push(renderComplexVisitSummary(data));
        break;
      case 'complexParticipants':
        parts.push(renderComplexParticipants(data));
        break;
      case 'cover':
        parts.push(renderCoverPage(data));
        break;
      case 'header':
        if (data.header) parts.push(renderHeader(data.header));
        break;
      case 'recipientBlock':
        if (data.recipientBlock) parts.push(renderRecipientBlock(data.recipientBlock));
        break;
      case 'subjectLine':
        if (data.subjectLine) parts.push(renderSubjectLine(data.subjectLine));
        break;
      case 'salutation':
        if (data.salutation) parts.push(renderSalutation(data.salutation));
        break;
      case 'bodyText':
        if (data.bodyParagraphs?.length) parts.push(renderBodyText(data.bodyParagraphs));
        break;
      case 'metadata':
        if (data.metadataFields?.length) parts.push(renderMetadata(data.metadataFields));
        break;
      case 'violations':
        parts.push(renderViolations(data));
        break;
      case 'photos':
        if (data.photos?.length) parts.push(renderPhotos(data.photos, data.documentConfig));
        break;
      case 'comments':
        if (data.comments) parts.push(renderComments(data.comments, (section as any).label));
        break;
      case 'contactBlock':
        if (data.contactBlock) parts.push(renderContactBlock(data.contactBlock));
        break;
      case 'signatures':
        if (data.signatures) parts.push(renderSignatures(data.signatures));
        break;
      case 'legal':
        if (data.legalText) parts.push(renderLegalText(data.legalText));
        break;
      case 'footer':
        if (data.footer) parts.push(renderFooter(data.footer));
        break;
      case 'staticContent':
        if (data.staticContent) parts.push(renderStaticContent(data.staticContent));
        break;
      default:
        break;
    }
  }

  // Advanced fields are rendered after all config sections (per universal.hbs pattern)
  if (data.renderAdvancedSection && data.advancedFields?.length) {
    parts.push(renderAdvancedFields(data.advancedFields, data.advancedSectionLabel));
  }

  // Form responses (worker-only, but rendered if present)
  if (data.formResponses?.length) {
    parts.push(renderFormResponses(data.formResponses));
  }

  return parts.join('\n');
}

// ---------------------------------------------------------------------------
// Band/flow rendering — A2 sibling driven by a DocumentLayout's band order
// (the canvas builder's model) instead of documentConfig.sections. Reuses the
// per-section renderers so output stays identical to the section-driven path.
// ---------------------------------------------------------------------------

/** True when a band should render given its `condition` and whether violations exist. */
function bandConditionMet(band: Band, hasViolations: boolean): boolean {
  switch (band.condition) {
    case 'hasViolations': return hasViolations;
    case 'noViolations': return !hasViolations;
    default: return true;
  }
}

/** Render a single band to HTML using resolved data (dynamic) or its own elements (static). */
function renderBandToHtml(band: Band, data: DocumentRenderData): string {
  switch (band.kind) {
    case 'letterhead': return data.header ? renderHeader(data.header) : '';
    case 'coverPage': return renderCoverPage(data);
    case 'recipientBlock': return data.recipientBlock ? renderRecipientBlock(data.recipientBlock) : '';
    case 'subjectLine': return data.subjectLine ? renderSubjectLine(data.subjectLine) : '';
    case 'salutation': return data.salutation ? renderSalutation(data.salutation) : '';
    case 'signatureBlock': return data.signatures ? renderSignatures(data.signatures) : '';
    case 'legal': return data.legalText ? renderLegalText(data.legalText) : '';
    case 'footer': return data.footer ? renderFooter(data.footer) : '';
    case 'metadataGrid': return data.metadataFields?.length ? renderMetadata(data.metadataFields) : '';
    case 'violationsList': return renderViolations(data);
    case 'photoGallery': return data.photos?.length ? renderPhotos(data.photos, data.documentConfig) : '';
    case 'formResponses': return data.formResponses?.length ? renderFormResponses(data.formResponses) : '';
    case 'advancedFields': return data.advancedFields?.length ? renderAdvancedFields(data.advancedFields, data.advancedSectionLabel) : '';
    // Static content bands render their own (pre-resolved, pre-sanitized) element HTML.
    // {{advField:fieldId}} tokens are substituted here so Canvas-authored content resolves
    // identically in both the PDF worker and the web preview (G-DRY-RENDER parity).
    case 'richText': {
      const rawHtml = band.elements.map((el) => el.html ?? '').filter(Boolean).join('\n');
      const html = data.advancedFields?.length
        ? resolveAdvancedTokensInHtml(rawHtml, data.advancedFields)
        : rawHtml;
      // Read from the FIRST text element, exactly as the canvas editor does
      // (DocumentCanvasBuilder setPrimaryTextStyle / find((el) => el.type === 'text')),
      // so the two surfaces can never select different elements.
      const elStyle = band.elements.find((el) => el.type === 'text')?.style;
      const styleAttr = elStyle ? ` style="${esc(elementStyleCss(elStyle))}"` : '';
      return html ? `<section class="report-section"><div class="body-paragraph"${styleAttr}>${html}</div></section>` : '';
    }
    case 'callout': {
      const rawHtml = band.elements.map((el) => el.html ?? '').filter(Boolean).join('\n');
      const html = data.advancedFields?.length
        ? resolveAdvancedTokensInHtml(rawHtml, data.advancedFields)
        : rawHtml;
      const elStyle = band.elements.find((el) => el.type === 'text')?.style;
      const calloutStyle = `border:1px solid var(--brand-primary,#ccc);border-radius:4px;padding:8pt;${elStyle ? elementStyleCss(elStyle) : ''}`;
      return `<section class="report-section"><div class="callout-box" style="${esc(calloutStyle)}">${html}</div></section>`;
    }
    case 'image': {
      const rawHtml = band.elements.map((el) => el.html ?? '').filter(Boolean).join('\n');
      const html = data.advancedFields?.length
        ? resolveAdvancedTokensInHtml(rawHtml, data.advancedFields)
        : rawHtml;
      return html ? `<section class="report-section">${html}</section>` : '';
    }
    case 'divider': return '<hr class="doc-divider" />';
    case 'spacer': return '<div class="doc-spacer" style="height:24px;"></div>';
    case 'pageBreak': return '<div style="page-break-before:always;break-before:page;"></div>';
    default: return '';
  }
}

/**
 * @process renderLayoutToHtml
 * Steps:   Render a DocumentLayout's bands in order (header band → body bands → footer band),
 *          skipping disabled bands and bands whose `condition` is not met, using resolved
 *          DocumentRenderData. Dynamic bands pull from the resolved data; static bands
 *          (richText/image/callout) render their own pre-resolved, pre-sanitized element HTML.
 * Inputs:  layout (band order), data (resolved render data).
 * Outputs: body HTML string (no page chrome).
 * Idempotency: REQUIRED (pure).
 */
export function renderLayoutToHtml(layout: DocumentLayout, data: DocumentRenderData): string {
  const hasViolations = data.hasViolations === true;
  const parts: string[] = [];
  const emit = (band: Band | null | undefined) => {
    if (!band || band.enabled === false || !bandConditionMet(band, hasViolations)) return;
    const html = renderBandToHtml(band, data);
    if (!html) return;
    // Apply per-band style (background/border/padding/columns) around the rendered
    // section so the data-driven path matches the layout-only path (G-RENDER-BG-01).
    // `keepTogether` forces the whole band onto one page (page-break-inside:avoid) so a
    // block is never split with a few orphan lines on the next page (G-PAGINATION-01).
    const declarations = band.style ? bandStyleCss(band.style) : '';
    const keepTogether = band.keepTogether ? 'page-break-inside:avoid;break-inside:avoid' : '';
    // Strip any trailing ';' before joining so the junction never produces ';;' (bandStyleCss can
    // end with one when it includes a border). A single separator yields well-formed inline CSS.
    const styleCss = [declarations, keepTogether].filter(Boolean).map((s) => s.replace(/;+$/, '')).join(';');
    if (!styleCss) { parts.push(html); return; }
    const attrs = band.keepTogether ? ' data-keep-together="true"' : '';
    parts.push(`<div class="doc-band-style" style="${esc(styleCss)}"${attrs}>${html}</div>`);
  };
  emit(layout.header);
  for (const band of layout.body) emit(band);
  emit(layout.footer);
  return parts.join('\n');
}
